import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { auroraStore, useAuroraStore } from '../store/auroraStore'
import { activeChild, activeChildId, activeThread, messagesFor, threads } from '../store/selectors'
import { Message } from '../components/thread/Message'
import { authorName, memberName, members } from '../store/selectors'
import { Composer } from '../components/thread/Composer'
import { ThreadPicker } from '../components/thread/ThreadPicker'
import { EmptyThread } from '../components/thread/EmptyThread'
import { CheckinBanner } from '../components/checkin/CheckinBanner'
import { CloseDay } from '../components/today/CloseDay'
import { reviewFor } from '../store/selectors'
import { todayISO } from '../lib/dates'
import { openGame } from '../lib/games'

/**
 * The thread — the substrate the whole app sits on.
 *
 * Everything else (Timeline, Story, Studio, Library) is a lens on what happens
 * here. If every lens were deleted the app would still work as a conversation;
 * delete this and there is no app. That ordering is the design. The day lives
 * here too: the morning brief shows the day's activities as cards, the week's
 * check-in waits at the top, and once the evening check-in opens, closing the
 * day sits at the end.
 */
export function ThreadView() {
  const child = useAuroraStore(activeChild)
  const childId = useAuroraStore(activeChildId)
  const thread = useAuroraStore(activeThread)
  const allThreads = useAuroraStore(threads)
  const messages = useAuroraStore((s: any) => messagesFor(s, thread?._id))
  const today = todayISO((child as any)?.timezone)
  const dayOpen = useAuroraStore((s: any) => !!reviewFor(s, 'day', today))

  const scrollRef = useRef<HTMLDivElement>(null)
  const [pinned, setPinned] = useState(true)
  const [pickerOpen, setPickerOpen] = useState(false)

  // Stay pinned to the bottom only while the reader is already there. Yanking
  // someone back down mid-scroll is the classic chat sin.
  const onScroll = () => {
    const el = scrollRef.current
    if (!el) return
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight
    setPinned(distance < 80)
  }

  useLayoutEffect(() => {
    const el = scrollRef.current
    if (el && pinned) el.scrollTop = el.scrollHeight
  }, [messages.length, pinned])

  // A streaming reply grows continuously; follow it only if pinned.
  const lastContent = (messages[messages.length - 1] as any)?.content
  useEffect(() => {
    const el = scrollRef.current
    if (el && pinned) el.scrollTop = el.scrollHeight
  }, [lastContent, pinned])

  if (!child || !childId) return null

  const send = (content: string, artifactIds?: string[]) => {
    ;(auroraStore.getState() as any).sendMessage({
      childId,
      threadId: thread?._id,
      content,
      artifactIds,
    })
    setPinned(true)
  }

  return (
    <div className="thread">
      <div className="thread-bar">
        {thread?.artifactId && (
          <button className="thread-bar-back" onClick={() => openGame(thread.artifactId)} aria-label="Back to the game" title="Back to the game">
            <BackIcon />
          </button>
        )}
        <button
          className="thread-bar-title"
          onClick={() => setPickerOpen((v) => !v)}
          aria-expanded={pickerOpen}
        >
          <span className="thread-bar-name">{thread?.title ?? 'New thread'}</span>
          <ChevronIcon />
        </button>
        <Roster threadId={thread?._id} />
        <button
          className="thread-bar-new"
          onClick={() => (auroraStore.getState() as any).newThread(childId)}
          aria-label="Start a new thread"
        >
          <PlusIcon />
        </button>
      </div>

      {pickerOpen && (
        <ThreadPicker
          threads={allThreads}
          activeId={thread?._id}
          onPick={(id) => {
            ;(auroraStore.getState() as any).selectThread(id)
            setPickerOpen(false)
            setPinned(true)
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}

      <div className="thread-scroll" ref={scrollRef} onScroll={onScroll}>
        <div className="thread-inner">
          <CheckinBanner />
          {messages.length === 0 ? (
            <EmptyThread childName={child.name} onPick={send} />
          ) : (
            messages.map((m: any, i: number) => (
              <Message key={m._id} message={m} isLast={i === messages.length - 1} />
            ))
          )}
          {dayOpen && <CloseDay date={today} today={today} />}
        </div>
      </div>

      {/* The way back down has its own lane above the composer, so it never sits on the words being read. */}
      <div className="thread-foot">
        {!pinned && messages.length > 0 && (
          <div className="thread-jump-lane">
            <button
              className="thread-jump"
              onClick={() => {
                const el = scrollRef.current
                if (el) el.scrollTop = el.scrollHeight
                setPinned(true)
              }}
            >
              Jump to latest
            </button>
          </div>
        )}
        <Composer onSend={send} childId={childId} />
      </div>
    </div>
  )
}

const ChevronIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M6 9l6 6 6-6" />
  </svg>
)

const BackIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M15 6l-6 6 6 6" />
  </svg>
)

const PlusIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 5v14M5 12h14" />
  </svg>
)

/**
 * Who is in this conversation: every parent on the account as an initial,
 * lit when they have written in this thread. Sapling is always here, so it
 * is not listed; the point is to see that the other parent is too.
 */
function Roster({ threadId }: { threadId?: string }) {
  const people = useAuroraStore((s: any) => {
    const all = members(s)
    if (all.length < 2) return []
    const wrote = new Set(
      Object.values(s.messages ?? {})
        .filter((m: any) => m?.threadId === threadId && m.role === 'user' && m.authorId)
        .map((m: any) => m.authorId),
    )
    return all.map((m) => ({
      id: m._id,
      name: authorName(s, m._id) ?? '',
      initial: (memberName(s, m._id) ?? '?').charAt(0).toUpperCase(),
      active: wrote.has(m._id),
    }))
  })
  if (!people.length) return null
  return (
    <div className="roster" aria-label="Who is in this thread">
      {people.map((p) => (
        <span
          key={p.id}
          className={`roster-chip${p.active ? ' is-active' : ''}`}
          title={p.active ? `${p.name} has written here` : p.name}
        >
          {p.initial}
        </span>
      ))}
    </div>
  )
}
